
import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchPublicProfile } from "@/services/profiles/publicProfile";
import { fetchUserReagents } from "@/services/profiles/fetchUserReagents";
import PublicProfileSection from "@/components/community/PublicProfileSection";
import UserReagentsSection from "@/components/community/UserReagentsSection";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/auth";

const PublicProfilePage: React.FC = () => {
  // Accept both parameter names (userId from community routes and id from profile routes)
  const { userId, id } = useParams<{ userId?: string; id?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Use whichever parameter is available (userId from community routes or id from profile routes)
  const profileId = userId || id;
  
  // Redirect to own profile if viewing self
  React.useEffect(() => {
    if (profileId === user?.id) {
      navigate("/profile");
    }
  }, [profileId, user, navigate]);
  
  const { 
    data: profile, 
    isLoading: isProfileLoading,
    error: profileError 
  } = useQuery({
    queryKey: ["public-profile", profileId],
    queryFn: () => fetchPublicProfile(profileId as string),
    enabled: !!profileId,
  });
  
  const {
    data: reagents = [],
    isLoading: isReagentsLoading,
  } = useQuery({
    queryKey: ["user-reagents", profileId],
    queryFn: () => fetchUserReagents(profileId as string),
    enabled: !!profileId,
  });
  
  if (isProfileLoading) {
    return (
      <div className="container py-8 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (profileError || !profile) {
    return (
      <div className="container py-8">
        <div className="text-center py-12 border rounded-lg">
          <h2 className="text-xl font-medium mb-2">User not found</h2>
          <p className="text-muted-foreground mb-4">This user profile does not exist or has been removed.</p>
          <Button asChild>
            <Link to="/community">Back to Community</Link>
          </Button>
        </div>
      </div>
    );
  }
  
  const displayName = profile.display_username || profile.username || "Anonymous User";

  return (
    <div className="container py-8">
      <div className="mb-6">
        <Button 
          variant="ghost" 
          asChild
          className="mb-4 -ml-3"
        >
          <Link to="/community">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Community
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">{displayName}'s Profile</h1>
      </div>
      
      <PublicProfileSection profile={profile} />
      
      <UserReagentsSection 
        reagents={reagents}
        isLoading={isReagentsLoading}
      />
    </div>
  );
};

export default PublicProfilePage;
